import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { confirmDelete } from '../../utils/confirm';

function BOSModal({ tenantId, bos, onClose, onSaved }) {
  const isEdit = !!bos;
  const [form, setForm] = useState({
    bosName: bos?.BOSName || '',
    isActive: bos ? bos.IsActive : true,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.bosName.trim()) return toast.error('BOS Name is required');

    setSaving(true);
    try {
      const payload = { tenantId, bosName: form.bosName.trim(), isActive: form.isActive };
      if (isEdit) {
        await api.put(`/bos/${bos.BOSID}`, payload);
        toast.success('BOS updated');
      } else {
        await api.post('/bos', payload);
        toast.success('BOS created');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>{isEdit ? 'Edit BOS' : 'Add BOS'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="required-label">BOS Name</label>
            <input
              type="text"
              placeholder="e.g., Computer Science BOS"
              value={form.bosName}
              onChange={(e) => setForm({ ...form, bosName: e.target.value })}
              autoFocus
            />
          </div>
          <div className="form-group">
            <label className="checkbox-inline">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
              Is Active
            </label>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-cancel-text" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-save" disabled={saving}>
              {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DefineBOSTab({ tenantId }) {
  const [bosList, setBOSList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editBOS, setEditBOS] = useState(null);

  const loadBOS = useCallback(async () => {
    if (!tenantId) return;
    try {
      setLoading(true);
      const res = await api.get(`/bos/list/${tenantId}`);
      if (res.data.success) setBOSList(res.data.data);
    } catch {
      toast.error('Failed to load BOS list');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { loadBOS(); }, [loadBOS]);

  const handleDelete = async (bosId) => {
    if (!(await confirmDelete('BOS'))) return;
    try {
      await api.delete(`/bos/${bosId}`);
      toast.success('BOS deleted');
      loadBOS();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  return (
    <>
      <div className="page-card-header" style={{ margin: 0, paddingBottom: 16, borderBottom: '1px solid #e0e0e0' }}>
        <h3>Board of Studies (BOS)</h3>
        <button className="btn btn-add" onClick={() => { setEditBOS(null); setShowModal(true); }}>
          + Add BOS
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Loading...</div>
      ) : bosList.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          No BOS defined yet. Click "Add BOS" to create one.
        </div>
      ) : (
        <table className="data-table" style={{ marginTop: 16 }}>
          <thead>
            <tr>
              <th>BOS Name</th>
              <th>Courses Allocated</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {bosList.map((b) => (
              <tr key={b.BOSID}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 18 }}>🏛</span>
                    <strong>{b.BOSName}</strong>
                  </div>
                </td>
                <td><span className="user-count-badge">{b.CourseCount}</span></td>
                <td>
                  <span className={`badge ${b.IsActive ? 'badge-active' : 'badge-inactive'}`}>
                    {b.IsActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button className="action-btn action-edit" title="Edit" onClick={() => { setEditBOS(b); setShowModal(true); }}>✏</button>
                    <button className="action-btn action-delete" title="Delete" onClick={() => handleDelete(b.BOSID)}>🗑</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showModal && (
        <BOSModal
          tenantId={tenantId}
          bos={editBOS}
          onClose={() => { setShowModal(false); setEditBOS(null); }}
          onSaved={() => { setShowModal(false); setEditBOS(null); loadBOS(); }}
        />
      )}
    </>
  );
}

export default DefineBOSTab;
